import { Link } from '@tanstack/react-router'

import { cn } from '@/lib/utils'
import { assetsGridContract } from '@/modules/data-grid'
import { HomeWidgetEmpty, HomeWidgetError, HomeWidgetLoading } from '@/ui/home-widget'

import { useAssetInventoryStats } from './asset-list-queries'
import { formatToken } from '../../lib/format-token'

const DEVICE_CLASS_LABELS: Readonly<Record<string, string>> = assetsGridContract.columns.deviceClass.labels ?? {}

export function AssetsSummaryWidget({ entityId }: { entityId: string }) {
  const stats = useAssetInventoryStats(entityId, true)
  if (stats.isPending) return <HomeWidgetLoading />
  if (stats.isError) return <HomeWidgetError onRetry={() => void stats.refetch()} />

  const { totalAssets, discoveredDevices, assetsByClass } = stats.data
  if (totalAssets === 0 && discoveredDevices === 0) return <HomeWidgetEmpty>No assets found yet.</HomeWidgetEmpty>

  return (
    <div className="space-y-4">
      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        <div>
          <dt className="text-muted-foreground text-xs">Assets</dt>
          <dd className="font-heading text-2xl font-semibold tabular-nums">{totalAssets}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Unrecognized</dt>
          <dd className="font-heading text-2xl font-semibold tabular-nums">
            <Link
              to="/$entityId/assets/unrecognized"
              params={{ entityId }}
              className={cn('rounded-md underline underline-offset-4', discoveredDevices > 0 && 'bg-warning/15 text-warning-foreground px-1.5')}
            >
              {discoveredDevices}
              <span className="sr-only"> unrecognized {discoveredDevices === 1 ? 'device' : 'devices'}</span>
            </Link>
          </dd>
        </div>
      </dl>
      {assetsByClass.length > 0 && (
        <ul aria-label="Assets by type" className="space-y-2 text-sm">
          {assetsByClass.map(({ deviceClass, count }) => (
            <li key={deviceClass} className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-3">
              <span className="min-w-0 break-words">{deviceClassLabel(deviceClass)}</span>
              <div aria-hidden="true" className="bg-muted h-2 overflow-hidden rounded-full">
                <div className="bg-primary h-full rounded-full" style={{ width: `${(count / totalAssets) * 100}%` }} />
              </div>
              <span className="tabular-nums">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function deviceClassLabel(deviceClass: string) {
  return Object.hasOwn(DEVICE_CLASS_LABELS, deviceClass) ? DEVICE_CLASS_LABELS[deviceClass] : formatToken(deviceClass)
}
